import { Fragment, type ReactNode, useState } from 'react'
import { Checkbox } from 'app/components/ui/checkbox'
import { laneSelectionState, toggleId, toggleLane } from 'lib/boardSelection'
import { ListBoardLane } from './ListBoardLane'
import { useTouchCardDrag } from './useTouchCardDrag'
import type {
  ListBoardCardDrag,
  ListBoardExitDrag,
  ListBoardLaneDef,
  ListBoardSelection,
  ListBoardTone,
  ListBoardWidth,
} from './boardTypes'

/**
 * The lanes of any board, and the drag state they share.
 *
 * It knows nothing about contacts, queues or pipelines. Where the items come from, what
 * a card looks like and what an exit looks like are the caller's. A drop on a lane, mouse
 * or finger, always goes to `onMove`. A finger drag can also end on a `data-board-drop`
 * element outside the lanes, such as a caller's exit zone; that key goes to `onExitDrop`
 * instead, since a mouse drop on an exit already reaches the caller through the exit's
 * own `onDrop`.
 */
export function ListBoard<T, K extends string = string>({
  lanes,
  itemId,
  renderCard,
  onMove,
  onExitDrop,
  renderExits,
  belowLanes,
  afterLanes,
  tone = `neutral`,
  width = `regular`,
  touchDrag = false,
  selection,
}: {
  lanes: ListBoardLaneDef<T, K>[]
  itemId: (item: T) => string | undefined
  renderCard: (item: T, drag: ListBoardCardDrag) => ReactNode
  onMove: (itemId: string, laneKey: K) => void
  /**
   * A finger drag that ends on a drop target outside the lanes, such as a caller's exit
   * zone. Mouse drops on an exit never come through here: the exit handles its own
   * `onDrop`.
   */
  onExitDrop?: (itemId: string, dropKey: string) => void
  renderExits?: (drag: ListBoardExitDrag) => ReactNode
  /** Between the lanes and the exits, such as a board-wide "Load more". */
  belowLanes?: ReactNode
  /** Inside the horizontal scroller after the last lane, such as an "Add stage" affordance. */
  afterLanes?: ReactNode
  tone?: ListBoardTone
  width?: ListBoardWidth
  /** Finger drags need cards that carry `data-board-card`. A board whose cards do not stays off. */
  touchDrag?: boolean
  /**
   * Set while the board is picking cards for a bulk action. A press on a card picks it
   * instead of opening it, each lane gets a checkbox for all of its cards, and nothing
   * drags: a drag would move one card while the picked ones sat still.
   */
  selection?: ListBoardSelection
}) {
  const [draggingId, setDraggingId] = useState<string>()
  const [boardEl, setBoardEl] = useState<HTMLElement | null>(null)
  const [touchOverKey, setTouchOverKey] = useState<string>()

  const drop = (droppedId: string | undefined, laneKey: K) => {
    setDraggingId(undefined)
    if (droppedId) onMove(droppedId, laneKey)
  }

  useTouchCardDrag(selection ? null : boardEl, {
    onLift: setDraggingId,
    onOver: setTouchOverKey,
    onDrop: (droppedId, dropKey) => {
      setTouchOverKey(undefined)
      const lane = lanes.find((candidate) => candidate.key === dropKey)
      if (lane) {
        drop(droppedId, lane.key)
      } else {
        setDraggingId(undefined)
        onExitDrop?.(droppedId, dropKey)
      }
    },
    onCancel: () => {
      setTouchOverKey(undefined)
      setDraggingId(undefined)
    },
  })

  return (
    // The negative margin gives back the page's own side padding and the padding inside puts
    // it straight back, so the lanes line up on the gutter the header uses while the
    // horizontal scrollbar and the cards' hover shadows get the full width to live in. Both
    // follow PageScroll's gutter: 16px on a phone, 40px from md up.
    <div ref={touchDrag ? setBoardEl : undefined} className="-mx-4 mt-5 flex min-h-0 flex-1 flex-col md:-mx-10">
      <div
        data-board-scroll={touchDrag || undefined}
        className="soft-scrollbar flex min-h-0 w-full flex-1 items-stretch gap-3 overflow-x-auto px-4 pb-1 md:px-10"
      >
        {lanes.map((lane) => {
          const laneIds = lane.items.flatMap((item) => itemId(item) ?? [])
          const laneState = selection ? laneSelectionState(selection.selected, laneIds) : `none`
          return (
            <ListBoardLane
              key={lane.key}
              laneKey={lane.key}
              label={lane.label}
              labelAdornment={lane.labelAdornment}
              selectControl={
                selection && (
                  <Checkbox
                    aria-label={`Select everyone in ${lane.label}`}
                    className="self-center"
                    disabled={laneIds.length === 0}
                    checked={laneState === `all` ? true : laneState === `some` ? `indeterminate` : false}
                    onCheckedChange={() => selection.onChange(toggleLane(selection.selected, laneIds))}
                  />
                )
              }
              count={lane.count}
              headerExtra={lane.headerExtra}
              headerAction={lane.headerAction}
              droppable={lane.droppable}
              isEmpty={lane.items.length === 0}
              emptyLabel={lane.emptyLabel}
              footer={lane.footer}
              tone={tone}
              width={width}
              solo={lanes.length === 1}
              touchDrop={touchDrag}
              touchOver={touchOverKey === lane.key}
              draggingId={draggingId}
              onDrop={(droppedId) => drop(droppedId, lane.key)}
            >
              {lane.items.map((item, index) => {
                const id = itemId(item)
                return (
                  <Fragment key={id ?? index}>
                    {renderCard(item, {
                      dragging: id !== undefined && draggingId === id,
                      onDragStart: () => setDraggingId(id),
                      onDragEnd: () => setDraggingId(undefined),
                      select:
                        selection && id !== undefined
                          ? {
                              selected: selection.selected.has(id),
                              onToggle: () => selection.onChange(toggleId(selection.selected, id)),
                            }
                          : undefined,
                    })}
                  </Fragment>
                )
              })}
              {lane.placeholders}
            </ListBoardLane>
          )
        })}
        {afterLanes}
      </div>

      {(renderExits || belowLanes) && (
        <div className="w-full shrink-0 px-4 md:px-10">
          {belowLanes}
          {renderExits && (
            // On a phone each drop target takes the lane's full width on a line of its own, so
            // it lines up with the lane above instead of squeezing its words into a column.
            <div className="mt-2 flex flex-wrap items-center gap-2 max-md:[&>*]:basis-full">
              {renderExits({ draggingId, touchOverKey, endDrag: () => setDraggingId(undefined) })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
